import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Plus, Search, X, Tag, Building2, Phone, Mail, MapPin, Coins, Calendar, AlertCircle, Trash2, EyeOff, Save, Briefcase, CheckCircle2, Clock, Edit3, Layers as LayersIcon, ListTodo, Upload, Webhook } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import HUDPageHeader from '../components/HUDPageHeader';
import Sparkline from '../components/Sparkline';
import CrmContactDrawer from '../components/crm/CrmContactDrawer';
import CrmContactForm from '../components/crm/CrmContactForm';
import CrmPipelineKanban from '../components/crm/CrmPipelineKanban';
import CrmImportDialog from '../components/crm/CrmImportDialog';
import CrmWebhooksPanel from '../components/crm/CrmWebhooksPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  nouveau:   { bg: 'bg-white/8', dot: 'bg-white/50', label: 'Nouveau' },
  prospect:  { bg: 'bg-amber-500/15', dot: 'bg-amber-400', label: 'Prospect' },
  qualifie:  { bg: 'bg-purple-500/15', dot: 'bg-purple-400', label: 'Qualifié' },
  client:    { bg: 'bg-emerald-500/15', dot: 'bg-emerald-400', label: 'Client' },
  perdu:     { bg: 'bg-red-500/15', dot: 'bg-red-400', label: 'Perdu' },
  archive:   { bg: 'bg-white/5', dot: 'bg-white/25', label: 'Archivé' },
};

const ROLE_LABELS = {
  acheteur: 'Acheteur',
  locataire: 'Locataire',
  investisseur: 'Investisseur',
  partenaire: 'Partenaire',
  architecte: 'Architecte',
  autre: 'Autre',
};

const CrmStatCard = ({ icon: Icon, label, value, sparkData, sparkColor = '#D9A05B', testid }) => (
  <div
    className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-4 backdrop-blur-md transition-all hover:border-white/20 overflow-hidden"
    data-testid={testid}
  >
    <div
      className="pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl opacity-40"
      style={{ background: `radial-gradient(circle, ${sparkColor}50 0%, ${sparkColor}00 65%)` }}
    />
    <div className="relative flex items-start justify-between mb-2">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${sparkColor}1F`, border: `1px solid ${sparkColor}3F` }}>
        <Icon className="w-4 h-4" style={{ color: sparkColor }} />
      </div>
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/35 mt-1">{label}</p>
    </div>
    <p className="relative text-3xl font-semibold text-white leading-none mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {value}
    </p>
    <Sparkline data={sparkData} color={sparkColor} height={28} testid={`${testid}-spark`} />
  </div>
);

const CrmContactCard = ({ contact, onClick, isOwner }) => {
  const status = STATUS_COLORS[contact.status] || STATUS_COLORS.nouveau;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`crm-contact-card-${contact.contact_id}`}
      className="text-left bg-white/[0.04] border border-white/10 rounded-xl p-4 hover:border-[#D9A05B]/40 hover:bg-white/[0.06] transition-all group w-full"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {contact.first_name}{contact.last_name ? ` ${contact.last_name}` : ''}
          </p>
          {contact.company && <p className="text-xs text-white/55 truncate flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" />{contact.company}</p>}
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${status.bg} text-[10px] uppercase tracking-widest font-bold text-white/85`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
      </div>
      <div className="space-y-1 text-xs text-white/55">
        {contact.email && <p className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" /> {contact.email}</p>}
        {contact.phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {contact.phone}</p>}
        {contact.budget_min_eur && (
          <p className="flex items-center gap-1.5 text-[#D9A05B]/80">
            <Coins className="w-3 h-3" /> {contact.budget_min_eur.toLocaleString('fr-FR')}€{contact.budget_max_eur ? ` – ${contact.budget_max_eur.toLocaleString('fr-FR')}€` : ''}
          </p>
        )}
      </div>
      {(contact.tags || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {contact.tags.slice(0, 3).map(t => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/60 border border-white/10">#{t}</span>
          ))}
        </div>
      )}
      {!isOwner && (
        <p className="mt-2 text-[10px] uppercase tracking-widest text-white/30 flex items-center gap-1"><EyeOff className="w-2.5 h-2.5" /> Lecture seule</p>
      )}
    </button>
  );
};

const CrmPage = () => {
  const [stats, setStats] = useState(null);
  const [timeseries, setTimeseries] = useState({});
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeView, setActiveView] = useState('contacts'); // 'contacts' | 'pipeline'
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showWebhooks, setShowWebhooks] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, tsRes, listRes, meRes] = await Promise.all([
        axios.get(`${API}/crm/stats`, { withCredentials: true }),
        axios.get(`${API}/crm/stats/timeseries?days=14`, { withCredentials: true }),
        axios.get(`${API}/crm/contacts?limit=200`, { withCredentials: true }),
        axios.get(`${API}/auth/me`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setTimeseries(tsRes.data?.series || {});
      setContacts(listRes.data?.items || []);
      setCurrentUserId(meRes.data?.user_id);
    } catch (e) {
      toast.error('Erreur de chargement CRM');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredContacts = contacts.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      const haystack = [c.first_name, c.last_name, c.email, c.company, c.phone].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });

  const handleSavedContact = () => {
    setShowCreateForm(false);
    setEditingContact(null);
    fetchAll();
  };

  return (
    <div data-hud-page data-testid="crm-page" className="fade-in">
      <HUDPageHeader
        module="Cockpit"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Mini-CRM' }]}
        title="Mini-CRM"
        subtitle="Centralisez prospects, opportunités et relances. Pipeline immobilier intégré."
        icon={Users}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setShowWebhooks(true)}
              variant="ghost"
              className="text-white/70 hover:text-[#E8B26C] border border-white/10 hover:border-[#D9A05B]/30"
              data-testid="crm-webhooks-btn"
            >
              <Webhook className="w-4 h-4 mr-2" /> Webhooks
            </Button>
            <Button
              onClick={() => setShowImport(true)}
              variant="ghost"
              className="text-white/70 hover:text-[#E8B26C] border border-white/10 hover:border-[#D9A05B]/30"
              data-testid="crm-import-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Importer
            </Button>
            <Button onClick={() => setShowCreateForm(true)} className="bg-[#D9A05B] hover:bg-[#C78D46] text-black" data-testid="crm-contact-create-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau contact
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* KPI top-row with sparklines */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 hud-stagger" data-testid="crm-stats-row">
            <CrmStatCard testid="crm-stats-total-contacts" icon={Users}     label="Contacts"      value={stats.total_contacts}   sparkData={timeseries.contacts_created} />
            <CrmStatCard testid="crm-stats-hot-leads"      icon={AlertCircle} label="Leads chauds"  value={stats.hot_leads_count} sparkColor="#F59E0B" sparkData={timeseries.opportunities_created} />
            <CrmStatCard testid="crm-stats-open-opps"      icon={Briefcase}   label="Opportunités" value={stats.open_opps_count}  sparkColor="#A855F7" sparkData={timeseries.opportunities_created} />
            <CrmStatCard testid="crm-stats-pipeline-amount" icon={Coins}      label="Pipeline €"   value={`${(stats.open_opps_amount_eur / 1000).toFixed(0)}k€`} sparkColor="#10B981" sparkData={timeseries.opportunities_won_amount_eur} />
          </div>
        )}

        {/* View tabs (Contacts / Pipeline) */}
        <div role="tablist" aria-label="Vue CRM" className="flex gap-1 bg-white/[0.04] border border-white/10 p-1 rounded-lg w-fit mb-6">
          <button
            role="tab"
            aria-selected={activeView === 'contacts'}
            onClick={() => setActiveView('contacts')}
            data-testid="crm-tab-contacts"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeView === 'contacts' ? 'bg-[#D9A05B]/15 text-[#E8B26C] border border-[#D9A05B]/30' : 'text-white/55 hover:text-white border border-transparent'}`}
          >
            <Users className="w-4 h-4 inline mr-1.5" />
            Contacts ({contacts.length})
          </button>
          <button
            role="tab"
            aria-selected={activeView === 'pipeline'}
            onClick={() => setActiveView('pipeline')}
            data-testid="crm-tab-pipeline"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeView === 'pipeline' ? 'bg-[#D9A05B]/15 text-[#E8B26C] border border-[#D9A05B]/30' : 'text-white/55 hover:text-white border border-transparent'}`}
          >
            <ListTodo className="w-4 h-4 inline mr-1.5" />
            Pipeline ({stats?.open_opps_count || 0})
          </button>
        </div>

        {activeView === 'contacts' && (
          <>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher nom, email, société…"
                  className="pl-9 h-9"
                  data-testid="crm-contact-search"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-white/5 border border-white/15 text-white text-sm rounded-lg h-9 px-3"
                data-testid="crm-contact-status-filter"
              >
                <option value="all">Tous les statuts</option>
                <option value="nouveau">Nouveau</option>
                <option value="prospect">Prospect</option>
                <option value="qualifie">Qualifié</option>
                <option value="client">Client</option>
                <option value="perdu">Perdu</option>
                <option value="archive">Archivé</option>
              </select>
            </div>

            {loading ? (
              <p className="text-center text-white/40 py-12">Chargement…</p>
            ) : filteredContacts.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-white/10 rounded-xl" data-testid="crm-contact-empty">
                <Users className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/55 text-sm">Aucun contact pour ces critères.</p>
                <Button onClick={() => setShowCreateForm(true)} variant="ghost" className="mt-3 text-[#D9A05B] hover:text-[#E8B26C]">
                  <Plus className="w-4 h-4 mr-1.5" /> Créer le premier contact
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="crm-contact-list">
                {filteredContacts.map(c => (
                  <CrmContactCard
                    key={c.contact_id}
                    contact={c}
                    isOwner={c.owner_user_id === currentUserId}
                    onClick={() => setSelectedContactId(c.contact_id)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeView === 'pipeline' && (
          <CrmPipelineKanban onSelectContact={(cid) => setSelectedContactId(cid)} onChange={fetchAll} />
        )}
      </div>

      {/* Drawer détail contact */}
      {selectedContactId && (
        <CrmContactDrawer
          contactId={selectedContactId}
          currentUserId={currentUserId}
          onClose={() => setSelectedContactId(null)}
          onChange={fetchAll}
          onEdit={(c) => { setSelectedContactId(null); setEditingContact(c); }}
        />
      )}

      {/* Modale création/édition */}
      {(showCreateForm || editingContact) && (
        <CrmContactForm
          existing={editingContact}
          onClose={() => { setShowCreateForm(false); setEditingContact(null); }}
          onSaved={handleSavedContact}
        />
      )}

      {/* Phase 2 — Import & Webhooks */}
      {showImport && (
        <CrmImportDialog
          onClose={() => setShowImport(false)}
          onImported={fetchAll}
        />
      )}
      {showWebhooks && (
        <CrmWebhooksPanel onClose={() => setShowWebhooks(false)} />
      )}
    </div>
  );
};

export default CrmPage;
